import { compact, difference, isEmpty } from 'lodash';

import { getTypeOrUndefined } from '@/modules/entities/EntitySubforms/utils/shared/common.utils';
import { StructuredAssetsSubform_ValuationFragment } from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { StructuredAsset } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  buildUpdateAssetStaticEntry,
  buildWithAssetStaticForCreate,
  filterEmptyStructuredAssetRows,
  getFormAssetsFromAssets,
  getIntegratedAssetsFromIntegratedValuation,
  valuationHasAdditionalManualAssets,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  buildBusinessEntityRealTppExposureUpdate,
  makeEntityTypeUniversalUpdateInput,
} from '@/modules/entities/utils/makeEntityTypeUniversalUpdateInput';
import {
  AssetValuationV2ValuationSource,
  AugmentedCreateAssetV2Input,
  AugmentedCreateAssetValuationV2Input,
  AugmentedUpdateEntityInput,
  EntityStage,
  IntegrationClientKind,
  UpdateEntityInput,
} from '@/types/schema';
import { assertExact } from '@/utils/assertUtils';
import { getNodes } from '@/utils/graphqlUtils';

import { AssetValueV2OwnershipType } from '../AssetValueOwnershipTypes';
import { FullScreenStructuredAssetsModalForm } from './FullScreenStructuredAssetsModal.types';

export function getIntegratedAssetsFromValuation(
  valuation: StructuredAssetsSubform_ValuationFragment | null
) {
  return getNodes(valuation?.assets).filter((a) => Boolean(a.integrationType));
}
export function getManuallyTrackedAssetsFromValuation(
  valuation: StructuredAssetsSubform_ValuationFragment | null
) {
  return getNodes(valuation?.assets).filter((a) => !Boolean(a.integrationType));
}

// getAssetIntegrationUpdateProperties is a function that returns a partial update entity input
// for properties that relate to the association of an entity with integration_entities.
function getAssetIntegrationUpdateProperties(
  formValues: FullScreenStructuredAssetsModalForm
): Partial<UpdateEntityInput> {
  const nextIntegrationEntityIds = compact(formValues.integrationEntityIds);
  const previousIntegrationEntityIds = compact(
    formValues._previousIntegrationEntityIds
  );
  const linkedToNonGrantorEntity = getTypeOrUndefined<boolean>(
    formValues.linkToAllAddeparEntities
  );
  const groupIntegrationAssetValues = !formValues.showAllIntegratedHoldings;

  const addedIntegrationEntityIds = difference(
    nextIntegrationEntityIds,
    previousIntegrationEntityIds
  );
  const removedIntegrationEntityIds = difference(
    previousIntegrationEntityIds,
    nextIntegrationEntityIds
  );

  const removalProperties = {
    removeIntegrationEntityIDs: removedIntegrationEntityIds,
  };

  if (!isEmpty(nextIntegrationEntityIds)) {
    return {
      addIntegrationEntityIDs: addedIntegrationEntityIds,
      ...removalProperties,
      addeparLinkedToNongrantorEntity: linkedToNonGrantorEntity,
      groupIntegrationAssetValues,
    };
  }

  return {
    // don't use `clearIntegrationEntities` here because it removes *all* integration entities
    // and we don't want to remove any potential CSV_IMPORT integration entities
    ...removalProperties,
    clearAddeparLinkedToNongrantorEntity: true,
    groupIntegrationAssetValues: true,
  };
}

// getBalanceSheetConfigurationUpdateProperties is a function that returns a partial update entity input
// for properties that relate to how this entity behaves in relation to the balance sheet feature.
function getBalanceSheetConfigurationUpdateProperties(
  formValues: FullScreenStructuredAssetsModalForm
): Partial<UpdateEntityInput> {
  if (
    !formValues.showBalanceSheetCategoryOverrideChooser ||
    !formValues.balanceSheetCategoryOverrideId
  ) {
    return {
      clearBalanceSheetCategoryOverride: true,
      showEntityNameOnBalanceSheet: formValues.showEntityNameOnBalanceSheet,
      liquidityStatus: formValues.liquidityStatus,
    };
  }

  return {
    balanceSheetCategoryOverrideID: formValues.balanceSheetCategoryOverrideId,
    showEntityNameOnBalanceSheet: formValues.showEntityNameOnBalanceSheet,
    liquidityStatus: formValues.liquidityStatus,
  };
}

function buildAssetV2RowInput(
  asset: StructuredAsset,
  params: { entityID: string; householdID: string }
): AugmentedCreateAssetV2Input {
  const renamed =
    !!asset.assetStaticID && asset.title !== asset.assetStaticDisplayName;
  const carryStaticID = !!asset.assetStaticID && !renamed;

  return {
    create: {
      classID: asset.categoryId,
      displayName: asset.title,
      householdID: params.householdID,
      integrationType: asset.integrationType ?? undefined,
      // Don't carry forward assetStaticID if the asset was renamed —
      // a name change means the user semantically replaced the asset.
      assetStaticID: carryStaticID ? asset.assetStaticID : undefined,
    },
    withAssetValue: {
      create: {
        householdID: params.householdID,
        ownedValue: asset.value,
        ownershipType: AssetValueV2OwnershipType.ValueBased,
      },
    },
    withAssetStatic: buildWithAssetStaticForCreate(asset, params) ?? undefined,
  };
}

// Targets the previous valuation: new valuation's rows link to the same AssetStatic via assetStaticID and inherit changes.
function buildSitusUpdateValuationInput(
  assets: StructuredAsset[],
  previousValuationId: string | null | undefined
) {
  if (!previousValuationId) return undefined;
  const updateAssets = assets
    .map(buildUpdateAssetStaticEntry)
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  if (updateAssets.length === 0) return undefined;
  return {
    id: previousValuationId,
    update: {},
    updateAssets,
  };
}

function getValuationSourceFromClientIntegrationKind(
  integrationType: IntegrationClientKind | null
) {
  switch (integrationType) {
    case IntegrationClientKind.Addepar:
      return AssetValuationV2ValuationSource.Addepar;
    case IntegrationClientKind.BlackDiamond:
      return AssetValuationV2ValuationSource.BlackDiamond;
    case IntegrationClientKind.Orion:
      return AssetValuationV2ValuationSource.Orion;
    case IntegrationClientKind.Emoney:
      return AssetValuationV2ValuationSource.Emoney;
    default:
      return AssetValuationV2ValuationSource.Manual;
  }
}

export interface CreateUpdateEntityAccountInputParams {
  activeClientIntegrationKind: IntegrationClientKind | null;
  previousValuation: StructuredAssetsSubform_ValuationFragment | null;
  entityId: string;
  entityType: EntityType;
  subtypeId: string;
  householdId: string;
  stage?: EntityStage;
}

export function createUpdateEntityAccountInput(
  formValues: FullScreenStructuredAssetsModalForm,
  params: CreateUpdateEntityAccountInputParams
): AugmentedUpdateEntityInput {
  if (!formValues.structuredAssetsSubform.accountId) {
    throw new Error('Missing required accountId');
  }

  if (!formValues.valuationAsOfDate) {
    throw new Error('Missing required valuationAsOfDate');
  }

  // A single empty row means "no assets of this kind" — drop empty rows so we
  // don't create junk assets, and so submitting an empty sole row clears them.
  const manualAssets = filterEmptyStructuredAssetRows(
    formValues.structuredAssetsSubform.assets
  );

  const entityUpdateProperties = assertExact<UpdateEntityInput>()({
    ...getBalanceSheetConfigurationUpdateProperties(formValues),
    ...getAssetIntegrationUpdateProperties(formValues),
    pauseIntegrationSync: formValues.pauseIntegrationSync,
    ...(params.stage && { stage: params.stage }),
  });

  // the tax discounts are defined on the subentity rather than the main entity, so we keep it separate from the above
  // shared entity update properties.
  const taxDiscountUpdate =
    formValues.showCurrentDiscountedValueOnBalanceSheet &&
    formValues.currentDiscountedValuePercentage
      ? {
          taxDiscount: formValues.currentDiscountedValuePercentage,
        }
      : {
          clearTaxDiscount: true,
        };

  const realTppExposureUpdate = buildBusinessEntityRealTppExposureUpdate(
    params.entityType,
    formValues.enableRealTppTaxExposure
  );

  // there is a pretty substantial difference between what we need to do for a linked integration vs. a manual valuation, so we just
  // handle them entirely separately.
  const isLinkedToIntegration = !isEmpty(formValues.integrationEntityIds);
  if (isLinkedToIntegration) {
    // we offer the ability for our customers to add additional, manually-entered assets to an integrated valuation.
    const previousValuationHadManualAssets = valuationHasAdditionalManualAssets(
      params.previousValuation
    );

    const nextEffectiveDate = formValues.valuationAsOfDate;
    const createNewValuationInput: AugmentedCreateAssetValuationV2Input | null =
      (() => {
        // this is the base case. we're not attemping to add additional manual assets, and the previous
        // valuation didn't have any so we don't need to worry about clearing them out. we can return early here because
        // we don't actually need to create a new valuation in this case; the user is just updating information around e.g.
        // tax discounts, integration entities, balance sheet config, etc.
        if (
          !formValues.allowAdditionalManualAssets &&
          !previousValuationHadManualAssets
        ) {
          return null;
        }

        // If we're here, we're creating a new valuation with additional manual assets!
        // Normally, valuations with integrated assets are created directly in the backend
        // via the sync jobs, but here we actually create them manually so we can shim in
        // the additional manual assets.

        // first, we need to get all of the assets that are already linked to the integration.
        // this can potentially be hundreds+ of assets, so we should be sure that we actually have them
        // all in the UI. (this is a little bit brittle, but it is what it is)
        // here, we're getting just the *integrated* assets from the previous valuation, and explicitly not
        // including any manual assets.
        const integratedAssets = getIntegratedAssetsFromIntegratedValuation(
          params.previousValuation
        );

        // then, combine the integrated assets with the additional manual assets that the user is adding.
        const assetsToAdd = [
          ...getFormAssetsFromAssets(integratedAssets, {
            activeClientIntegrationType: params.activeClientIntegrationKind,
          }),
          // don't add additional manual assets if we're not allowing them
          ...(formValues.allowAdditionalManualAssets ? manualAssets : []),
        ];

        return {
          create: {
            effectiveDate: nextEffectiveDate,
            householdID: params.householdId,
            valuationSource: getValuationSourceFromClientIntegrationKind(
              params.activeClientIntegrationKind
            ),
          },
          withAssets: assetsToAdd.map((a) =>
            buildAssetV2RowInput(a, {
              entityID: params.entityId,
              householdID: params.householdId,
            })
          ),
        };
      })();

    const integratedSitusUpdate = buildSitusUpdateValuationInput(
      manualAssets,
      params.previousValuation?.id
    );

    return makeEntityTypeUniversalUpdateInput(
      params.entityId,
      params.entityType,
      {
        id: params.subtypeId,
        update: {
          ...taxDiscountUpdate,
          ...realTppExposureUpdate,
        },
        updateDesignerAccount: {
          id: formValues.structuredAssetsSubform.accountId,
          update: {},
          withValuations: createNewValuationInput
            ? [createNewValuationInput]
            : undefined,
          updateValuations: integratedSitusUpdate
            ? [integratedSitusUpdate]
            : undefined,
        },
      },
      entityUpdateProperties
    );
  }

  // if we're here, it means we're just creating a new manual asset valuation.
  const manualSitusUpdate = buildSitusUpdateValuationInput(
    manualAssets,
    params.previousValuation?.id
  );

  const updateEntityAccountInput: AugmentedUpdateEntityInput =
    makeEntityTypeUniversalUpdateInput(
      params.entityId,
      params.entityType,
      {
        id: params.subtypeId,
        update: {
          ...taxDiscountUpdate,
          ...realTppExposureUpdate,
        },
        updateDesignerAccount: {
          id: formValues.structuredAssetsSubform.accountId,
          update: {},
          // if linked to an integration, don't allow adding new valuations manually.
          // this isn't generally an issue, but can be if moving *from* a manual valuation
          // scenario to an integrated valuation scenario.
          withValuations: [
            {
              create: {
                effectiveDate: formValues.valuationAsOfDate,
                description: formValues.structuredAssetsSubform.description,
                householdID: params.householdId,
                valuationSource: AssetValuationV2ValuationSource.Manual,
                documentIDs: formValues.structuredAssetsSubform.documentIds,
              },
              withAssets: manualAssets.map((a) =>
                buildAssetV2RowInput(a, {
                  entityID: params.entityId,
                  householdID: params.householdId,
                })
              ),
            },
          ],
          updateValuations: manualSitusUpdate ? [manualSitusUpdate] : undefined,
        },
      },
      entityUpdateProperties
    );

  return updateEntityAccountInput;
}
